import { ReactNode } from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress,
  Box,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

type ButtonSize = 'sm' | 'md' | 'lg' | 'xl';

interface ExtendedButtonProps {
  variant?: MuiButtonProps['variant'];
  size?: ButtonSize;
  isFullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  sx?: SxProps<Theme>;
}

export interface ButtonProps
  extends Omit<MuiButtonProps, 'variant' | 'size' | 'sx' | 'fullWidth'>,
    ExtendedButtonProps {}

const getSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'sm':
      return {
        padding: '6px 12px',
        fontSize: '0.875rem',
        minHeight: '32px',
      };
    case 'md':
      return {
        padding: '8px 16px',
        fontSize: '1rem',
        minHeight: '40px',
      };
    case 'lg':
      return {
        padding: '12px 24px',
        fontSize: '1.125rem',
        minHeight: '48px',
      };
    case 'xl':
      return {
        padding: '16px 32px',
        fontSize: '1.25rem',
        minHeight: '56px',
      };
    default:
      return {
        padding: '8px 16px',
        fontSize: '1rem',
        minHeight: '40px',
      };
  }
};

export default function Button({
  children,
  variant = 'contained',
  size = 'md',
  isFullWidth = false,
  isLoading = false,
  leftIcon,
  rightIcon,
  disabled,
  sx,
  ...props
}: ButtonProps) {
  const sizeStyles = getSizeStyles(size);

  return (
    <MuiButton
      variant={variant}
      fullWidth={isFullWidth}
      disabled={disabled || isLoading}
      startIcon={!isLoading && leftIcon}
      endIcon={!isLoading && rightIcon}
      sx={[
        {
          // Custom size styles override theme defaults when needed
          ...sizeStyles,
          // Loading state styles
          ...(isLoading && {
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
              display: 'none',
            },
          }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} color="inherit" />
          {children}
        </Box>
      ) : (
        children
      )}
    </MuiButton>
  );
}
